import { useEffect, useRef, lazy, Suspense } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from './context/AuthContext'
import { AppConfigProvider } from './context/AppConfigContext'
import { ThemeProvider, useTheme } from './themes/ThemeProvider'
import { MapProvider } from './context/MapContext'
import { ImmersiveProvider } from './context/ImmersiveContext'
import { LoadingProvider } from './context/LoadingContext'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import { trackPageView, trackLanding, getTrafficSource } from './utils/analytics'
import useOnboardingHydration from './hooks/useOnboardingHydration'

// Lazy load all route components for better code splitting
// Auth Pages
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'))

// Entity Management Pages
const AddEntity = lazy(() => import('./pages/AddEntity'))
const EditEntity = lazy(() => import('./pages/EditEntity'))
const CheckIn = lazy(() => import('./pages/CheckIn'))

// Other Pages
const Contact = lazy(() => import('./pages/Contact'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const CityLanding = lazy(() => import('./pages/CityLanding'))
const CityList = lazy(() => import('./pages/CityList'))
const CityPost = lazy(() => import('./pages/CityPost'))
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'))
const EventPage = lazy(() => import('./pages/EventPage'))
const NotFound = lazy(() => import('./pages/NotFound'))
const SightingDetailPage = lazy(() => import('./pages/SightingDetailPage'))
const EntityQRPage = lazy(() => import('./pages/EntityQRPage'))
const EntityWidgetPage = lazy(() => import('./pages/EntityWidgetPage'))

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))
const EntitiesPage = lazy(() => import('./pages/admin/EntitiesPage'))
const SightingsPage = lazy(() => import('./pages/admin/SightingsPage'))
const ContactsPage = lazy(() => import('./pages/admin/ContactsPage'))
const AnnouncementsPage = lazy(() => import('./pages/admin/AnnouncementsPage'))
const AnnouncementForm = lazy(() => import('./pages/admin/AnnouncementForm'))
const InvitesPage = lazy(() => import('./pages/admin/InvitesPage'))
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'))
const ReviewsPage = lazy(() => import('./pages/admin/ReviewsPage'))
const DevicesPage = lazy(() => import('./pages/admin/DevicesPage'))
const CitiesPage = lazy(() => import('./pages/admin/CitiesPage'))
const CityPostsPage = lazy(() => import('./pages/admin/CityPostsPage'))
const EventsPage = lazy(() => import('./pages/admin/EventsPage'))
const EventFormPage = lazy(() => import('./pages/admin/EventFormPage'))
const ListingsPage = lazy(() => import('./pages/admin/ListingsPage'))
const OfferingsPage = lazy(() => import('./pages/admin/OfferingsPage'))
const UserDetail = lazy(() => import('./pages/admin/UserDetail'))
const EntityDetailAdmin = lazy(() => import('./pages/admin/EntityDetail'))
const SightingDetail = lazy(() => import('./pages/admin/SightingDetail'))
const ContactDetail = lazy(() => import('./pages/admin/ContactDetail'))

// Loading fallback component
const PageLoader = () => {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">{t('app.loading')}</p>
      </div>
    </div>
  )
}

function AppContent() {
  const location = useLocation()
  const { currentTheme, themeId } = useTheme()
  const { isAuthenticated } = useAuth()
  const lastTrackedPath = useRef(null)

  // Sync server-side onboarding state to localStorage once per session.
  // Must be here (not in OnboardingProgress) so hydration runs as soon as the
  // user is authenticated — regardless of whether the banner is visible.
  useOnboardingHydration()

  // Track session landing once per session (captures UTM params + referrer for attribution)
  useEffect(() => {
    trackLanding(getTrafficSource())
  }, [])

  // Track page views on route change (prevent duplicates in StrictMode)
  useEffect(() => {
    const currentPath = location.pathname + location.search
    
    // Only track if this is a new path (prevents StrictMode double-firing)
    if (lastTrackedPath.current !== currentPath) {
      lastTrackedPath.current = currentPath
      trackPageView(currentPath, document.title)
    }
  }, [location])

  // Get immersive theme page components
  const HomePage = currentTheme.components.Home
  const EntityDetailPage = currentTheme.components.EntityDetail
  const LoginPage = currentTheme.components.Login
  const RegisterPage = currentTheme.components.Register

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage key={`home-${themeId}`} />} />
            <Route path="/login" element={<LoginPage key={`login-${themeId}`} />} />
            <Route path="/register" element={<RegisterPage key={`register-${themeId}`} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/entity/:id" element={<EntityDetailPage key={`entity-${themeId}`} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/city" element={<CityList />} />
            <Route path="/city/:citySlug" element={<CityLanding />} />
            <Route path="/city/:citySlug/:postSlug" element={<CityPost />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/events/:id" element={<EventPage />} />
            <Route path="/sightings/:id" element={<SightingDetailPage />} />
            <Route path="/entity/:id/qr" element={<EntityQRPage />} />
            <Route path="/widget/entity/:id" element={<EntityWidgetPage />} />
            
            {/* Protected routes */}
            <Route path="/add-entity" element={
              <ProtectedRoute>
                <AddEntity />
              </ProtectedRoute>
            } />
            <Route path="/entity/:id/edit" element={
              <ProtectedRoute>
                <EditEntity />
              </ProtectedRoute>
            } />
            <Route path="/entity/:id/sighting" element={
              <ProtectedRoute>
                <CheckIn />
              </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireAdmin>
                <UsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/:id" element={
              <ProtectedRoute requireAdmin>
                <UserDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/entities" element={
              <ProtectedRoute requireAdmin>
                <EntitiesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/entities/:id" element={
              <ProtectedRoute requireAdmin>
                <EntityDetailAdmin />
              </ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute requireAdmin>
                <ReviewsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/devices" element={
              <ProtectedRoute requireAdmin>
                <DevicesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/sightings" element={
              <ProtectedRoute requireAdmin>
                <SightingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/sightings/:id" element={
              <ProtectedRoute requireAdmin>
                <SightingDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/contacts" element={
              <ProtectedRoute requireAdmin>
                <ContactsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/contacts/:id" element={
              <ProtectedRoute requireAdmin>
                <ContactDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/announcements" element={
              <ProtectedRoute requireAdmin>
                <AnnouncementsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/announcements/:id" element={
              <ProtectedRoute requireAdmin>
                <AnnouncementForm />
              </ProtectedRoute>
            } />
            <Route path="/admin/notifications" element={
              <ProtectedRoute requireAdmin>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/onboarding" element={<Navigate to="/admin/invites" replace />} />
            <Route path="/admin/cities" element={
              <ProtectedRoute requireAdmin>
                <CitiesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/cities/:cityId/posts" element={
              <ProtectedRoute requireAdmin>
                <CityPostsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/invites" element={
              <ProtectedRoute requireAdmin>
                <InvitesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/events" element={
              <ProtectedRoute requireAdmin>
                <EventsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/events/new" element={
              <ProtectedRoute requireAdmin>
                <EventFormPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/events/:id" element={
              <ProtectedRoute requireAdmin>
                <EventFormPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/listings" element={
              <ProtectedRoute requireAdmin>
                <ListingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/offerings" element={
              <ProtectedRoute requireAdmin>
                <OfferingsPage />
              </ProtectedRoute>
            } />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <LoadingProvider>
        <AuthProvider>
          <AppConfigProvider>
            <ThemeProvider>
              <MapProvider mode="general">
                <ImmersiveProvider>
                  <AppContent />
                </ImmersiveProvider>
              </MapProvider>
            </ThemeProvider>
          </AppConfigProvider>
        </AuthProvider>
      </LoadingProvider>
    </ErrorBoundary>
  )
}

export default App